/* eslint-disable @typescript-eslint/no-unused-vars */
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@libs/zapi-ui/ds/components/select";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle
} from "@libs/zapi-ui/ds/components/sheet";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { ComponentProps, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getMemberRoles, memberStatuses, getAccessModes } from "../const/const";
import { TUser, TAdminProject, TUserRole, TAccessMode } from "../services/schemas";
import { toast } from "sonner";
import { updateUser } from "../services/updateUser";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { hasEditAccess } from "@/zapi-frontend/shared/utils/permissions";

export interface ManageAccessDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: TUser;
  availableProjects: TAdminProject[];
  orgId: number;
  onSuccess?: () => void;
}

export function ManageAccessDrawer({
  open,
  onOpenChange,
  user,
  availableProjects,
  orgId,
  onSuccess,
}: ManageAccessDrawerProps) {
  const { t } = useTranslation(["settings", "common"]);
  const { role: currentUserRole, accessMode: currentUserAccessMode, id: currentUserId } = useMeStore();
  const currentUserHasEditAccess = hasEditAccess(currentUserRole, currentUserAccessMode);
  const [currentRole, setCurrentRole] = useState<string>("");
  const [currentAccessMode, setCurrentAccessMode] = useState<string>("");
  const [currentStatus, setCurrentStatus] = useState<string>("");
  const [projectSearch, setProjectSearch] = useState<string>("");
  const [selectedProjectIds, setSelectedProjectIds] = useState<number[]>([]);

  useEffect(() => {
    if (user) {
      setCurrentRole(user.role);
      setCurrentAccessMode(user.accessMode);
      setCurrentStatus(user.status);
      setSelectedProjectIds(user.projects?.map((p) => p.id) || []);
    } else {
      setCurrentRole("");
      setCurrentAccessMode("");
      setCurrentStatus("");
      setSelectedProjectIds([]);
    }
  }, [user, open]);

  const handleRoleChange = (value: string) => {
    setCurrentRole(value);
    if (value === "systemAdmin") {
      setCurrentAccessMode("edit");
    }
  };

  const handleAccessModeChange = (value: string) => {
    setCurrentAccessMode(value);
  };

  const handleStatusChange = (value: string) => {
    setCurrentStatus(value);
  };

  // const handleProjectSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   setProjectSearch(e.target.value);
  //   // TODO: Implement actual project filtering based on search
  // };

  // const handleAddProject = () => {};

  // const handleProjectSelect = (projectId: number) => {
  //   setSelectedProjectIds((prevIds) =>
  //     prevIds.includes(projectId) ? prevIds.filter((id) => id !== projectId) : [...prevIds, projectId]
  //   );
  // };

  const isAccessModeDisabled = !currentUserHasEditAccess || user?.id === currentUserId;

  const [isSaving, setIsSaving] = useState(false);
  const handleSave = async () => {
    if (currentRole === user.role && currentAccessMode === user.accessMode && currentStatus === user.status) {
      toast.message(t("manageAccess.noChanges"));
      onOpenChange(false);
      return;
    }
    try {
      setIsSaving(true);
      await updateUser({
        orgId,
        userId: user.id,
        role: currentRole as TUserRole,
        accessMode: currentAccessMode as TAccessMode,
        active: currentStatus === "active"
      });
      onSuccess?.();
      toast.success(t("manageAccess.updated"));
    } catch (error) {
      console.error("Error updating user:", error);
      toast.error(t("manageAccess.updateFailed"));
    } finally {
      setIsSaving(false);
      onOpenChange(false);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  // Filter projects based on search (simple implementation for now)
  const displayedProjects = availableProjects.filter((p) => p.name.toLowerCase().includes(projectSearch.toLowerCase()));

  const roles = getMemberRoles(t, currentUserRole);
  const accessModes = getAccessModes(t);
  const statuses = memberStatuses(t);

  return (
    // The root stays mounted so opening animates; Base UI skips the transition for a sheet that mounts open.
    <Sheet open={open && !!user} onOpenChange={(next) => onOpenChange(next)}>
      {user && (
        <SheetContent
          side="right"
          showCloseButton={false}
          overlayClassName="bg-black/80 duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] supports-backdrop-filter:backdrop-blur-none"
          className="gap-0 border bg-background text-base shadow-none duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] data-ending-style:opacity-100 data-starting-style:opacity-100 data-[side=right]:w-full data-[side=right]:data-ending-style:translate-x-full data-[side=right]:data-starting-style:translate-x-full data-[side=right]:sm:max-w-[520px]"
        >
          <SheetHeader className="grid gap-1.5 border-b p-4 text-center sm:text-start">
            <SheetTitle className="font-sans text-lg leading-none font-medium tracking-tight">
              {t("manageAccess.title", { name: `${user.firstName} ${user.lastName}` })}
            </SheetTitle>
          </SheetHeader>
          <div className="flex flex-1 flex-col gap-6 overflow-y-auto px-6 py-4">
            <div className="grid gap-2">
              <span className="mb-1 text-[14px] font-medium">{t("manageAccess.memberRole")}</span>
              <Select
                items={roles}
                value={currentRole || null}
                onValueChange={(value) => value !== null && handleRoleChange(value)}
              >
                <SelectTrigger data-testid="manage-access-role-trigger">
                  <SelectValue placeholder={t("manageAccess.selectRole")} />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((r) => (
                    <SelectItem key={r.value} value={r.value} data-testid={`manage-access-role-${r.value}-item`}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {currentRole !== "systemAdmin" && (
              <div className="grid gap-2">
                <span className="mb-1 text-[14px] font-medium">{t("manageAccess.accessMode")}</span>
                <Select
                  items={accessModes}
                  value={currentAccessMode || null}
                  onValueChange={(value) => value !== null && handleAccessModeChange(value)}
                  disabled={isAccessModeDisabled}
                >
                  <SelectTrigger data-testid="manage-access-access-mode-trigger">
                    <SelectValue placeholder={t("manageAccess.selectAccessMode")} />
                  </SelectTrigger>
                  <SelectContent>
                    {accessModes.map((m) => (
                      <SelectItem
                        key={m.value}
                        value={m.value}
                        data-testid={`manage-access-access-mode-${m.value}-item`}
                      >
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid gap-2">
              <span className="mb-1 text-[14px] font-medium">{t("manageAccess.memberStatus")}</span>
              <Select
                items={statuses}
                value={currentStatus || null}
                onValueChange={(value) => value !== null && handleStatusChange(value)}
              >
                <SelectTrigger data-testid="manage-access-status-trigger">
                  <SelectValue placeholder={t("manageAccess.selectStatus")} />
                </SelectTrigger>
                <SelectContent>
                  {statuses.map((st) => (
                    <SelectItem key={st.value} value={st.value} data-testid={`manage-access-status-${st.value}-item`}>
                      {st.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* <div className="flex items-center gap-2">
            <SearchInput
              placeholder="Search Projects"
              value={projectSearch}
              onChange={handleProjectSearch}
              className="flex-1 w-full"
            />
            <Button onClick={handleAddProject} variant="default">
              <Plus />
              Project
            </Button>
          </div> */}
            {/* <div className="rounded-lg border">
            <div className="flex items-center px-4 py-2 border-b text-[14px] font-medium text-zinc-700">
              <Checkbox className="mr-2" disabled />
              Projects
              <span className="ml-1">↓</span>
            </div>
            <div>
              {displayedProjects.length > 0 ? (
                displayedProjects.map((project) => (
                  <div key={project.id} className="flex items-center px-4 py-3 border-b last:border-b-0 gap-3">
                    <Checkbox
                      checked={selectedProjectIds.includes(project.id)}
                      onCheckedChange={() => handleProjectSelect(project.id)}
                      className="mr-3"
                      aria-label={`Select project ${project.name}`}
                    />
                    <div className="bg-zinc-900 rounded-md w-10 h-10 flex items-center justify-center mr-3"></div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{project.name}</div>
                    </div>

                    <Button size="icon" variant="ghost">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </div>
                ))
              ) : (
                <div className="p-4 text-center text-[14px] text-zinc-500">No projects found.</div>
              )}
            </div>

            <div className="flex items-center justify-between px-4 py-2 border-t text-[14px]">
              <div className="flex items-center gap-1">
                <Button size="icon" variant="ghost" className="w-7 h-7 p-0" disabled>
                  &lt;
                </Button>
                <span className="font-medium">1</span>
                <Button size="icon" variant="ghost" className="w-7 h-7 p-0" disabled>
                  &gt;
                </Button>
              </div>
              <span className="text-zinc-500">
                Showing {displayedProjects.length} of {availableProjects.length} results
              </span>
            </div>
          </div> */}
          </div>
          <SheetFooter className="flex flex-row gap-2 border-t p-4">
            <Button type="button" data-testid="manage-access-save-button" onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Spinner data-icon="inline-start" aria-label={t("common:loading")} />
                  Loading...
                </>
              ) : (
                t("manageAccess.saveRole")
              )}
            </Button>
            <SheetClose
              render={<Button type="button" variant="secondary" />}
              data-testid="manage-access-cancel-button"
              onClick={handleCancel}
            >
              {t("common.cancel")}
            </SheetClose>
          </SheetFooter>
        </SheetContent>
      )}
    </Sheet>
  );
}
